import { useEffect, useState } from "react";
import { Check, Sparkles } from "lucide-react";
import { useI18n } from "@/i18n";
import { api } from "@/lib/api";

export function usePlans() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/billing/plans").then((r) => setData(r.data)).catch(() => setData({ items: [], trial_images: 0 })); }, []);
  return data;
}

export function IntervalToggle({ value, onChange, disabled }) {
  const { t } = useI18n();
  return (
    <div className="inline-flex rounded-full border border-[var(--line)] bg-[var(--bg-2)] p-1" data-testid="plan-interval-toggle">
      {["monthly", "yearly"].map((k) => (
        <button key={k} type="button" disabled={disabled && value !== k} onClick={() => onChange(k)} data-testid={`plan-interval-${k}`}
          className={`rounded-full px-5 py-1.5 text-sm font-medium transition-colors disabled:opacity-40 ${value === k ? "bg-emerald-600 text-white" : "text-neutral-300 hover:text-white"}`}>
          {t(`plans.${k}`)}
        </button>
      ))}
    </div>
  );
}

function PlanCard({ p, interval, action }) {
  const { t, fmtMoney, fmtNum } = useI18n();
  const price = interval === "yearly" ? p.price_yearly : p.price_monthly;
  return (
    <div className={`light-panel relative p-6 flex flex-col transition-transform duration-200 hover:-translate-y-1 ${p.popular ? "ring-2 ring-emerald-500 shadow-2xl" : ""}`} data-testid={`plan-card-${p.id}`}>
      {p.popular && (
        <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-semibold text-white" data-testid={`plan-popular-${p.id}`}>
          <Sparkles size={12} />{t("plans.popular")}
        </span>
      )}
      <p className="text-sm font-semibold text-emerald-700" data-testid={`plan-name-${p.id}`}>{p.name}</p>
      <p className="mt-3 flex items-end gap-1">
        {price ? <>
          <span className="font-display text-4xl font-semibold" data-testid={`plan-price-${p.id}`}>{fmtMoney(price, p.currency)}</span>
          <span className="text-neutral-500 mb-1">{t(interval === "yearly" ? "plans.perYear" : "plans.perMonth")}</span>
        </> : <span className="font-display text-2xl font-semibold text-neutral-400 h-10 flex items-end" data-testid={`plan-price-${p.id}`}>{t("plans.monthlyOnly")}</span>}
      </p>
      <p className="mt-2 text-sm font-semibold" data-testid={`plan-credits-${p.id}`}>{t("plans.credits", { n: fmtNum(p.credits_per_month) })}</p>
      <ul className="mt-5 space-y-2 text-sm text-neutral-700 flex-1">
        {t("landing.planItems").map((x) => <li key={x} className="flex gap-2"><Check size={16} className="text-emerald-600 shrink-0 mt-0.5" />{x}</li>)}
      </ul>
      <div className="mt-6">{action(p, price)}</div>
    </div>
  );
}

export function PlanGrid({ plans, interval, action }) {
  const { t } = useI18n();
  if (!plans.length) return <p className="text-sm text-neutral-400" data-testid="plans-empty">{t("plans.none")}</p>;
  return (
    <div className={`grid gap-5 sm:grid-cols-2 ${plans.length >= 4 ? "xl:grid-cols-4" : "lg:grid-cols-3"}`} data-testid="plan-grid">
      {plans.map((p) => <PlanCard key={p.id} p={p} interval={interval} action={action} />)}
    </div>
  );
}
